import React from 'react';
import SubmissionRow from './SubmissionRow';
import type { StageActionLog } from './SubmissionRow';
import SubmissionDetails from './SubmissionDetails';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { Submission } from 'types/evaluation';

const PreSelectionSubmissionsTable = ({
  submissions,
  expandedRows,
  toggleRowExpansion,
  sortField,
  sortDirection,
  handleSort,
  getMainDescription,
  sanitizeAIText,
  showStageAction = false,
  onStageChange,
  onRecalculateComplete,
}: {
  submissions: Submission[];
  expandedRows: Set<string | number>;
  toggleRowExpansion: (id: string | number) => void;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (field: string) => void;
  getMainDescription: (submission: Submission) => string;
  sanitizeAIText: (text: string | null | undefined) => string;
  showStageAction?: boolean;
  onStageChange?: (
    documentId: string,
    newStage: string,
    updatedLogs?: StageActionLog[]
  ) => void;
  onRecalculateComplete?: () => void;
}) => {
  const SortIndicator = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpIcon className="w-3 h-3 text-gray-300 opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUpIcon className="w-3 h-3 text-gray-900" />
    ) : (
      <ArrowDownIcon className="w-3 h-3 text-gray-900" />
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed min-w-[1200px] xl:min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-8 py-3 px-2" style={{ width: '4%' }}></th>
              <th className="text-left py-3 px-2 font-bai font-medium text-sm text-gray-700">
                <button
                  onClick={() => handleSort('full_name')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Submitter <SortIndicator field="full_name" />
                </button>
              </th>
              <th className="text-left py-3 px-2 font-bai font-medium text-sm text-gray-700">
                <button
                  onClick={() => handleSort('email')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Email <SortIndicator field="email" />
                </button>
              </th>
              <th
                className="text-left py-3 px-2 font-bai font-medium text-sm text-gray-700"
                style={{ width: '12%' }}
              >
                <button
                  onClick={() => handleSort('pillar')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Pillar <SortIndicator field="pillar" />
                </button>
              </th>
              <th
                className="text-left py-3 px-2 font-bai font-medium text-sm text-gray-700"
                style={{ width: '12%' }}
              >
                <button
                  onClick={() => handleSort('category')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Category <SortIndicator field="category" />
                </button>
              </th>
              <th className="text-left py-3 px-2 font-bai font-medium text-sm text-gray-700">
                <button
                  onClick={() => handleSort('organization_unit')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Organization <SortIndicator field="organization_unit" />
                </button>
              </th>
              <th
                className="text-left py-3 px-2 font-bai font-medium text-sm text-gray-700"
                style={{ width: '15%' }}
              >
                <button
                  onClick={() => handleSort('submitted_date')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Submission Date <SortIndicator field="submitted_date" />
                </button>
              </th>
              <th
                className="text-center py-3 px-2 font-bai font-medium text-sm text-gray-700"
                style={{ width: '9%' }}
              >
                <button
                  onClick={() => handleSort('total_score')}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors mx-auto"
                >
                  Score <SortIndicator field="total_score" />
                </button>
              </th>
              {showStageAction && (
                <>
                  <th className="text-center py-3 px-2 font-bai font-medium text-sm text-gray-700 w-24 border-l border-gray-100">
                    Stage
                  </th>
                  <th className="text-center py-3 px-2 font-bai font-medium text-sm text-gray-700 w-26">
                    Action
                  </th>
                  <th className="text-center py-3 px-2 font-bai font-medium text-sm text-gray-700 w-24 border-l border-gray-100">
                    Material
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {submissions.map((submission: Submission, index: number) => (
              <React.Fragment key={submission.id}>
                <SubmissionRow
                  index={index}
                  submission={submission}
                  expanded={expandedRows.has(submission.id)}
                  toggleRowExpansion={toggleRowExpansion}
                  showSubmitterName={true}
                  showStageAction={showStageAction}
                  onStageChange={onStageChange}
                  fromPage="preselectionToQualification"
                  showSubmissionDate={true}
                  onRecalculateComplete={onRecalculateComplete}
                  showCategory={true}
                />
                {expandedRows.has(submission.id) && (
                  <SubmissionDetails
                    submission={submission}
                    getMainDescription={getMainDescription}
                    sanitizeAIText={sanitizeAIText}
                    colSpan={showStageAction ? 11 : 8}
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreSelectionSubmissionsTable;
